/**
 * verificationTestApi — Verification API Testing (BACKLOG-1743, Bill 2026-10-06; transparency
 * Bill 2026-10-07).
 *
 * Risk Management runs the same LexisNexis identity check and the same OFAC check the FSR app
 * uses, on demand, and sees what came back and WHY it reads Clear / Historical / Hit. RiskManager
 * role only; every run is a real inquiry recorded with the caller and the stated purpose.
 *
 *   GET  /v3/risk/verification-test/explain
 *   POST /v3/risk/verification-test/lexisnexis  { subject? | cifId?, purpose, environment }
 *   POST /v3/risk/verification-test/ofac        { name, nameType, purpose }
 *   POST /v3/risk/verification-test/customers   { query }
 *   GET  /v3/risk/verification-test/cif/{cifId}
 *   PUT  /v3/risk/settings                      { settingKey, settingValue, reason }
 *
 * Every field typed here is one the server (V3RiskVerificationTestFunction) actually returns.
 */

import { buildHeaders, getBaseUrl, readJsonOrThrow } from '@/services/riskSettingsApi';

export type Tone = 'ok' | 'warn' | 'err' | 'info' | 'muted';

export interface SettingUsed {
  key: string;
  value: string | null;
  source: 'saved' | 'default';
  owner: string;
  effect: string;
}

export interface CoverageCount {
  name: string;
  count: number;
  countsAsCurrent?: boolean | null;
}

export interface SdnCoverage {
  totalEntities: number;
  currentListEntities: number;
  aliasRows: number;
  bySource: CoverageCount[];
  lists: CoverageCount[];
  programs: CoverageCount[];
  treasuryDataAsOf: string | null;
  lastSyncedAt: string | null;
}

export interface OfacSearchService {
  name: string;
  listsSearched: string;
  listsSource: string;
  matching: string;
  matchingSource: string;
  score: string;
  input: string;
  maxRecords: number;
  nameTypeNote: string;
}

export interface SyncRun {
  startedAt: string;
  finishedAt: string | null;
  status: string;
  syncMode: string;
  entitiesUpserted: number | null;
  entitiesDeleted: number | null;
  triggerSource?: string;
}

export interface ExplainResponse {
  success: boolean;
  fraudPoint: {
    lowFloor: number;
    moderateFloor: number;
    procedureLowFloor: number;
    procedureModerateFloor: number;
    marketBasis: string;
    enforcement: { strategy: string; citationKey: string; isSubtypeOverride: boolean; customerType: string } | null;
  };
  lexisNexis: {
    enabled: string | null;
    enabledModes: string | null;
    enabledOfficers: string | null;
    policyName: string;
    policyNameSandbox: string;
    serviceType: string;
    eventType: string;
    products: string[];
    watchlistIncluded: boolean;
    requiredInputs: string;
  };
  ofac: {
    jhaSearch: string;
    cacheEnabled: string | null;
    cacheSyncFailed: string | null;
    cacheEntities: number;
    treasuryDataAsOf: string | null;
    lastSyncRun: SyncRun | null;
    lastSuccessfulSync: string | null;
    recentRuns: SyncRun[];
    coverage: SdnCoverage;
    searchService: OfacSearchService;
    fsrOverrideEnabled: boolean;
    rescreenStaleAfterDays: string | null;
  };
  settings: SettingUsed[];
}

export interface TestSubject {
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  ssn?: string;
  dob?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

export interface MaskedSubject extends Omit<TestSubject, 'ssn'> {
  taxIdLast4: string | null;
}

export interface RuleStep {
  text: string;
  applied: boolean;
}

export interface LnVerdict {
  band: string | null;
  tone: Tone;
  plain: string;
  commitText: string;
  officerReviewChip: string;
  officerGuidanceLabel: string;
  officerReportLabel: string;
  fixedScreensDiffer: boolean;
  fixedScreensNote: string | null;
  rules: RuleStep[];
}

export interface LnTransparency {
  live: boolean;
  billed: boolean;
  cartModeUsed: string;
  productsReturned: string[];
  verdict: LnVerdict;
  gate: {
    band: string | null;
    market: string;
    marketBasis: string;
    requirementText: string;
    requirementUnmet: boolean;
    strategy: string | null;
    citationKey: string | null;
    strategyIsSubtypeOverride: boolean;
    wouldBlock: boolean;
    customerType: string;
    assumes: string;
  };
  watchlistFields: Array<{ key: string; value: string }>;
  settings: SettingUsed[];
}

export interface LnTestResponse {
  success: boolean;
  inquiryId?: string;
  extractId?: string;
  error?: string;
  errorCode?: string;
  note?: string | null;
  subject?: MaskedSubject;
  subjectSource?: 'entered' | 'core-director' | 'core-director-edited';
  editedFields?: string[];
  environment?: 'production' | 'sandbox';
  mock?: boolean;
  policyName?: string | null;
  transactionStatus?: string | null;
  productStatus?: string | null;
  reasonCode?: string | null;
  reasonDescription?: string | null;
  fraudPoint?: {
    score: number | null;
    band: string | null;
    lowFloor: number;
    moderateFloor: number;
    indices: Record<string, number | null>;
  };
  instantId?: {
    verificationRiskIndex: number | null;
    nameAddressPhone: string | null;
    nameAddressSsn: string | null;
    dobMatchLevel: string | null;
  };
  emailage?: { score: number | null; advice: string | null; called: boolean };
  riskCodesJson?: string | null;
  potentialRiskIndicatorsJson?: string | null;
  transparency?: LnTransparency;
  rawResponse?: string | null;
}

export interface OfacMatch {
  matchType: string;
  aliasName: string;
  aliasId: string;
  matchRef: string;
  specDesigNatName?: string | null;
  specDesigNatType?: string | null;
  specDesigNatId?: string | null;
  specDesigNatPgm?: string | null;
  specDesigNatAddr?: string | null;
  specDesigNatCity?: string | null;
  specDesigNatCntry?: string | null;
  remarks?: string[];
}

export interface OfacClassification {
  aliasId: string;
  classification: 'current' | 'archived-only' | 'unknown' | string;
  isCurrentSdn: boolean;
  entityId?: string | null;
  primaryName?: string | null;
  programs?: string[];
  legalAuthorities?: string[];
  dob?: string | null;
  placeOfBirth?: string | null;
  gender?: string | null;
  akaResolved?: boolean;
  akaName?: string | null;
  akaType?: string | null;
  akaCategory?: string | null;
}

export interface TokenMatch {
  inputToken: string;
  soundex: string;
  kind: 'same' | 'sounds-alike' | 'none';
  listedToken: string | null;
}

export interface CandidateRow {
  matchType: string;
  matchTypeLabel: string;
  aliasName: string;
  aliasId: string;
  matchRef: string;
  aliasKind: string | null;
  tokens: TokenMatch[];
  standing: 'current' | 'delisted' | 'unknown';
  standingReason: string;
}

export interface CandidateGroup {
  key: string;
  listedName: string;
  listedType: string | null;
  treasuryEntryId: string | null;
  programs: string[];
  standing: 'current' | 'delisted' | 'unknown';
  details: string[];
  remarks: string[];
  rows: CandidateRow[];
  nameCheckNote: string | null;
}

export interface OfacVerdict {
  status: string;
  tone: Tone;
  officerPill: string;
  officerBanner: string | null;
  commitAllowed: boolean;
  commitText: string;
  plain: string;
}

export interface OfacTransparency {
  live: boolean;
  searchService: OfacSearchService;
  request: { comName: string; comNameType: string; count: number };
  returned: { sentRec: number; totRec: number | null; moreRec: boolean | null; capped: boolean };
  path: string;
  sdnCopy: {
    totalEntities: number;
    currentListEntities: number;
    aliasRows: number;
    lists: CoverageCount[];
    treasuryDataAsOf: string | null;
    lastSyncedAt: string | null;
    programCount: number;
  };
  groups: CandidateGroup[];
  rules: RuleStep[];
  verdict: OfacVerdict;
  settings: SettingUsed[];
}

export interface OfacTestResponse {
  success: boolean;
  error?: string;
  name?: string;
  nameType?: string;
  status?: string;
  elapsedMs?: number;
  matches?: OfacMatch[];
  classifications?: OfacClassification[];
  transparency?: OfacTransparency;
  rawResponse?: string | null;
}

export async function loadExplain(): Promise<ExplainResponse> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/explain`, { headers: await buildHeaders() });
  return readJsonOrThrow<ExplainResponse>(resp);
}

/** LexisNexis returns success:false with a reason (e.g. the API declined) — that is a result, not a throw. */
export async function runLexisNexisTest(body: { subject?: TestSubject; cifId?: string; purpose: string; environment: 'production' | 'sandbox' }): Promise<LnTestResponse> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/lexisnexis`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  const text = await resp.text();
  let parsed: LnTestResponse | null = null;
  try { parsed = text ? (JSON.parse(text) as LnTestResponse) : null; } catch { parsed = null; }
  if (resp.status === 403) throw new Error('Your account is not set up for Risk Management testing (RiskManager role required).');
  if (!parsed) throw new Error(`HTTP ${resp.status}`);
  if (!resp.ok && !parsed.inquiryId) throw new Error(parsed.error ?? `HTTP ${resp.status}`);
  return parsed;
}

export async function runOfacTest(body: { name: string; nameType: 'Indv' | 'Org'; purpose: string }): Promise<OfacTestResponse> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/ofac`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  return readJsonOrThrow<OfacTestResponse>(resp);
}

/** Starts a full Treasury SDN load in the background; progress shows in loadExplain().ofac.lastSyncRun. */
export async function startSdnRefresh(): Promise<void> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/sdn-refresh`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: '{}',
  });
  await readJsonOrThrow<{ success: boolean; error?: string }>(resp);
}

/**
 * Save one Risk-owned setting with the reason for the change (recorded with the change).
 * Goes through the Risk settings route; the server decides which keys Risk may change.
 */
export async function saveRiskSettingWithReason(settingKey: string, settingValue: string, reason: string): Promise<void> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/settings`, {
    method: 'PUT',
    headers: await buildHeaders(),
    body: JSON.stringify({ settingKey, settingValue, reason }),
  });
  await readJsonOrThrow<{ success: boolean; error?: string }>(resp);
}

export interface CustomerHit {
  cifId: string;
  name: string;
  custType: string;
  taxIdLast4: string | null;
}

export async function searchCustomers(query: string): Promise<CustomerHit[]> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/customers`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ query }),
  });
  const body = await readJsonOrThrow<{ success: boolean; customers?: CustomerHit[]; error?: string }>(resp);
  return body.customers ?? [];
}

/** What Core Director holds for the CIF; the tax ID comes back as the last 4 only. */
export async function loadCifPrefill(cifId: string): Promise<MaskedSubject> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification-test/cif/${encodeURIComponent(cifId)}`, {
    headers: await buildHeaders(),
  });
  const body = await readJsonOrThrow<{ success: boolean; subject?: MaskedSubject; error?: string }>(resp);
  if (!body.subject) throw new Error('Core Director returned nothing for that CIF.');
  return body.subject;
}
